import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, OnChanges, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import {
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    isWeekend,
    isWithinInterval,
    startOfDay,
    startOfMonth,
    startOfWeek
} from 'date-fns';
import { CalendarDay, CalendarView, UserEvent, Week } from '../../models/out-of-office-overview.model';

@Component({
    selector: 'app-out-of-office-overview-grid',
    standalone: true,
    templateUrl: './out-of-office-overview-grid.component.html',
    imports: [TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeOverviewGridComponent implements OnChanges {
    private readonly translateService = inject(TranslateService);

    public readonly isDataLoading = input.required<boolean>();
    public readonly calendarView = input<CalendarView>('month');
    public readonly date = input.required<Date>();
    public readonly userEvents = input.required<UserEvent[]>();
    public readonly selectedUserEvent = input.required<UserEvent | null>();

    private readonly currentLang = toSignal(
        this.translateService.onLangChange.pipe(
            map((event) => event.lang),
            startWith(this.translateService.currentLang || this.translateService.getDefaultLang() || 'en')
        ),
        { initialValue: this.translateService.currentLang || this.translateService.getDefaultLang() || 'en' }
    );

    protected readonly weeks = signal<Week>([]);
    protected readonly minimumRows = signal<number>(4);
    protected readonly weekdays = computed<string[]>(() => {
        void this.currentLang();
        return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    });

    protected readonly Array = Array;

    public ngOnChanges(): void {
        this.generateCalendarView();
        this.mapUserDataToCalendar();
    }

    protected isToday(date: Date): boolean {
        const today = new Date();
        return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate()
        );
    }

    protected isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    protected isShowOnDate(event: UserEvent, date: Date): boolean {
        return event.showOn.some((showDate) => this.isSameDay(showDate, date));
    }

    protected isEventSelected(event: UserEvent, selected: UserEvent | null): boolean {
        return selected !== null && event.id === selected.id;
    }

    protected getMinimumRowCount(week: CalendarDay[]): number {
        const maxUsersInWeek = Math.max(...week.map((day) => day.users.length), 0);
        return Math.max(this.minimumRows(), maxUsersInWeek);
    }

    private generateCalendarView(): void {
        if (this.calendarView() === 'month') {
            const days = this.generateMonthlyView(this.date());
            this.generateDayMatrix(days);
        } else {
            const days = this.generateWeeklyView(this.date());
            this.generateDayMatrix(days);
        }
    }

    private generateMonthlyView(date: Date): Date[] {
        this.minimumRows.set(4);
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(monthStart);
        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
        return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).filter((d) => !isWeekend(d));
    }

    private generateWeeklyView(date: Date): Date[] {
        this.minimumRows.set(15);
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        return eachDayOfInterval({ start: weekStart, end: weekEnd }).filter((d) => !isWeekend(d));
    }

    private generateDayMatrix(days: Date[]): void {
        const result: CalendarDay[][] = [];
        let week: CalendarDay[] = [];

        for (const day of days) {
            week.push({ day, users: [] });
            if (week.length === 5) {
                result.push(week);
                week = [];
            }
        }

        if (week.length > 0) {
            result.push(week);
        }

        this.weeks.set(result);
    }

    protected mapUserDataToCalendar(): void {
        for (const userEvent of this.userEvents()) {
            const eventStart = startOfDay(userEvent.startDate);
            const eventEnd = startOfDay(userEvent.endDate);
            const showOnDates: Date[] = [];

            for (const week of this.weeks()) {
                let weekHasEvent = false;

                for (const calendarDay of week) {
                    if (
                        calendarDay &&
                        isWithinInterval(startOfDay(calendarDay.day), { start: eventStart, end: eventEnd })
                    ) {
                        const userIndex = this.getUserIndexForDay(
                            userEvent,
                            week,
                            week.indexOf(calendarDay),
                            this.weeks().indexOf(week)
                        );

                        if (!calendarDay.users.some((user) => user === userEvent)) {
                            calendarDay.users[userIndex] = userEvent;

                            if (!weekHasEvent) {
                                const date = this.showUserNameInEvents(week, calendarDay, eventStart, eventEnd);
                                if (date) {
                                    showOnDates.push(date);
                                }
                                weekHasEvent = true;
                            }
                        }
                    }
                }
            }

            userEvent.showOn = showOnDates;
        }
    }

    private getUserIndexForDay(user: UserEvent, week: CalendarDay[], dayIndex: number, weekIndex: number): number {
        let userIndex = 0;
        let userFoundInPreviousDay = false;

        if (dayIndex > 0 && week[dayIndex - 1]) {
            const previousDayUsers = week[dayIndex - 1].users;
            if (previousDayUsers && previousDayUsers.length > 0) {
                const previousDayUser = previousDayUsers.find((u) => u === user);
                if (previousDayUser) {
                    userIndex = previousDayUsers.indexOf(previousDayUser);
                    userFoundInPreviousDay = true;
                }
            }
        }

        if (!userFoundInPreviousDay && weekIndex > 0) {
            const previousWeek = this.weeks()[weekIndex - 1];
            const lastDayPreviousWeek = previousWeek[previousWeek.length - 1];
            if (lastDayPreviousWeek && lastDayPreviousWeek.users.length > 0) {
                const lastWeekUser = lastDayPreviousWeek.users.find((u) => u === user);
                if (lastWeekUser) {
                    userIndex = lastDayPreviousWeek.users.indexOf(lastWeekUser);
                    userFoundInPreviousDay = true;
                }
            }
        }

        if (!userFoundInPreviousDay) {
            while (week[dayIndex].users[userIndex]) {
                userIndex++;
            }
        }

        return userIndex;
    }

    private showUserNameInEvents(
        week: CalendarDay[],
        calendarDay: CalendarDay,
        startDate: Date,
        endDate: Date
    ): Date | null {
        const daysInWeek = week.filter((d) => d && isWithinInterval(d.day, { start: startDate, end: endDate })).length;
        const middleDayIndex = Math.floor(daysInWeek / 2);
        const calendarDayIndex = week.indexOf(calendarDay);
        const showOnDate = week[calendarDayIndex + middleDayIndex]
            ? week[calendarDayIndex + middleDayIndex].day
            : calendarDay.day;
        return showOnDate || null;
    }
}
